import { NavLink, Outlet } from "react-router";
import { useEffect, useRef, type ButtonHTMLAttributes, type InputHTMLAttributes, type ReactNode, type SelectHTMLAttributes } from "react";

export const cx = (...c: (string | false | null | undefined)[]) => c.filter(Boolean).join(" ");

type Variant = "primary" | "secondary" | "ghost" | "danger";
const variants: Record<Variant, string> = {
  primary: "bg-[var(--chrome)] text-white shadow-sm hover:bg-[var(--chrome-2)]",
  secondary: "border border-[var(--line)] bg-[var(--panel)] hover:bg-[var(--panel-2)]",
  ghost: "hover:bg-[var(--panel-2)] text-[var(--muted)] hover:text-[var(--text)]",
  danger: "border border-red-500/30 text-red-700 hover:bg-red-500/10",
};

export function Button({ variant = "secondary", className, ...p }: ButtonHTMLAttributes<HTMLButtonElement> & { variant?: Variant }) {
  return (
    <button
      {...p}
      className={cx(
        "inline-flex h-9 items-center justify-center gap-2 rounded-lg px-3.5 text-sm font-medium transition disabled:pointer-events-none disabled:opacity-50",
        variants[variant],
        className,
      )}
    />
  );
}

const field = "h-9 w-full rounded-lg border border-[var(--line)] bg-[var(--panel)] px-3 text-sm outline-none transition placeholder:text-[var(--muted)] focus:border-[var(--color-accent)] focus:ring-2 focus:ring-[var(--color-accent)]/20";

export function Input({ className, ...p }: InputHTMLAttributes<HTMLInputElement>) {
  return <input {...p} className={cx(field, className)} />;
}

export function Select({ className, ...p }: SelectHTMLAttributes<HTMLSelectElement>) {
  return <select {...p} className={cx(field, "pr-8", className)} />;
}

export function Field({ label, hint, children }: { label: string; hint?: ReactNode; children: ReactNode }) {
  return (
    <label className="block space-y-1.5">
      <span className="text-sm font-medium">{label}</span>
      {children}
      {hint && <span className="block text-xs text-[var(--muted)]">{hint}</span>}
    </label>
  );
}

export function Switch({ checked, onChange, label, hint, disabled }: { checked: boolean; onChange: (v: boolean) => void; label: string; hint?: string; disabled?: boolean }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      disabled={disabled}
      onClick={() => onChange(!checked)}
      className="flex w-full items-start justify-between gap-4 rounded-lg py-2 text-left disabled:opacity-50"
    >
      <span>
        <span className="block text-sm font-medium">{label}</span>
        {hint && <span className="block text-xs text-[var(--muted)]">{hint}</span>}
      </span>
      <span className={cx("relative mt-0.5 h-5 w-9 shrink-0 rounded-full transition", checked ? "bg-[var(--color-accent)]" : "bg-[var(--line)]")}>
        <span className={cx("absolute top-0.5 h-4 w-4 rounded-full bg-white shadow transition-all", checked ? "left-[18px]" : "left-0.5")} />
      </span>
    </button>
  );
}

export function Card({ className, children }: { className?: string; children: ReactNode }) {
  return <div className={cx("rounded-xl border border-[var(--line)] bg-[var(--panel)]", className)}>{children}</div>;
}

const tones = {
  neutral: "bg-[var(--panel-2)] text-[var(--muted)]",
  accent: "bg-sky-500/10 text-sky-700",
  good: "bg-emerald-500/10 text-emerald-700",
  bad: "bg-red-500/10 text-red-700",
};
export function Badge({ tone = "neutral", children }: { tone?: keyof typeof tones; children: ReactNode }) {
  return <span className={cx("inline-flex items-center rounded-md px-2 py-0.5 text-xs font-medium", tones[tone])}>{children}</span>;
}

export function PageHeader({ title, description, actions }: { title: string; description?: string; actions?: ReactNode }) {
  return (
    <div className="mb-6 flex flex-wrap items-end justify-between gap-4">
      <div>
        <h1 className="text-xl font-semibold tracking-tight">{title}</h1>
        {description && <p className="mt-1 text-sm text-[var(--muted)]">{description}</p>}
      </div>
      {actions && <div className="flex flex-wrap items-center gap-2">{actions}</div>}
    </div>
  );
}

/** Native <dialog> — focus trap, Esc and backdrop for free. */
export function Dialog({ open, onClose, title, children, wide }: { open: boolean; onClose: () => void; title: ReactNode; children: ReactNode; wide?: boolean | "xl" }) {
  const ref = useRef<HTMLDialogElement>(null);
  useEffect(() => {
    const d = ref.current;
    if (!d) return;
    if (open && !d.open) d.showModal();
    if (!open && d.open) d.close();
  }, [open]);
  return (
    <dialog
      ref={ref}
      onClose={onClose}
      onClick={(e) => e.target === ref.current && onClose()}
      className={cx(
        "m-auto w-[calc(100%-2rem)] rounded-2xl border border-[var(--line)] bg-[var(--panel)] p-0 text-[var(--text)] shadow-2xl backdrop:bg-black/50 backdrop:backdrop-blur-sm",
        wide === "xl" ? "max-w-4xl" : wide ? "max-w-2xl" : "max-w-md",
      )}
    >
      {open && (
        <div className="max-h-[85vh] overflow-y-auto p-6">
          <h2 className="mb-5 text-lg font-semibold">{title}</h2>
          {children}
        </div>
      )}
    </dialog>
  );
}

export function ErrorText({ error }: { error: unknown }) {
  if (!error) return null;
  return <p className="rounded-lg bg-red-500/10 px-3 py-2 text-sm text-red-700">{error instanceof Error ? error.message : String(error)}</p>;
}

export function Spinner() {
  return <div className="h-5 w-5 animate-spin rounded-full border-2 border-[var(--line)] border-t-[var(--color-accent)]" />;
}

/** A page split into tabs; each tab is its own route (so refresh, back and links keep the tab). */
export function TabbedLayout({ tabs }: { tabs: { to: string; label: string }[] }) {
  return (
    <>
      <nav aria-label="Sections" className="-mx-4 mb-6 flex gap-1 overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden border-b border-[var(--line)] px-4 md:mx-0 md:px-0">
        {tabs.map((t) => (
          <NavLink
            key={t.to}
            to={t.to}
            className={({ isActive }) =>
              cx(
                "-mb-px shrink-0 whitespace-nowrap border-b-2 px-3.5 py-2.5 text-sm transition",
                isActive ? "border-[var(--color-accent)] font-medium text-[var(--text)]" : "border-transparent text-[var(--muted)] hover:text-[var(--text)]",
              )
            }
          >
            {t.label}
          </NavLink>
        ))}
      </nav>
      <Outlet />
    </>
  );
}

/** "26–50 of 130", Previous / Next and a page-size choice, for lists paged on the server. */
export function Pagination({ page, pageSize, total, onPage, onPageSize, busy }: { page: number; pageSize: number; total: number; onPage: (p: number) => void; onPageSize?: (n: number) => void; busy?: boolean }) {
  const pages = Math.max(1, Math.ceil(total / pageSize));
  if (total <= 0 || (total <= pageSize && !onPageSize)) return null;
  const from = (page - 1) * pageSize + 1;
  const to = Math.min(total, page * pageSize);
  // The list is above this bar: after turning a page, bring its top back into view.
  const go = (p: number, e: React.MouseEvent<HTMLElement>) => {
    const card = e.currentTarget.closest("[data-pager]")?.parentElement;
    onPage(p);
    card?.scrollIntoView({ block: "start", behavior: "smooth" });
  };
  return (
    <div data-pager className="flex flex-wrap items-center justify-between gap-3 border-t border-[var(--line)] px-4 py-3 text-sm text-[var(--muted)]">
      <span className={cx(busy && "opacity-60")}>
        {from.toLocaleString()}–{to.toLocaleString()} of {total.toLocaleString()}
      </span>
      <div className="flex items-center gap-2">
        {onPageSize && (
          <select value={pageSize} onChange={(e) => onPageSize(Number(e.target.value))} aria-label="Rows per page" className="h-8 rounded-lg border border-[var(--line)] bg-[var(--panel)] px-2 text-xs">
            {[10, 25, 50, 100].map((n) => (
              <option key={n} value={n}>{n} per page</option>
            ))}
          </select>
        )}
        <Button className="h-8" disabled={page <= 1} onClick={(e) => go(page - 1, e)}>Previous</Button>
        <span className="min-w-20 text-center text-xs">Page {page} of {pages.toLocaleString()}</span>
        <Button className="h-8" disabled={page >= pages} onClick={(e) => go(page + 1, e)}>Next</Button>
      </div>
    </div>
  );
}
